import { combine, withCallbacks, withContext } from 'react-hooks-combine'
import { withFormModel } from 'shared/hooks'
import { ServicesContext } from 'services/context'
import { spec, validators } from 'utils/validation'
import EditTournamentFormComponent from './component'

const { any } = validators

// Sports enum
const SPORTS = [
  { value: 'BASKETBALL', label: 'Basketball' },
  { value: 'BASEBALL', label: 'Baseball' },
  { value: 'SOCCER', label: 'Soccer' },
  { value: 'HOCKEY', label: 'Hockey' },
  { value: 'FOOTBALL', label: 'Football' },
  { value: 'FOOTBALL_NZ', label: 'Football NZ' },
  { value: 'ATHLETICS', label: 'Athletics' },
  { value: 'OTHER', label: 'Other' },
]

export const EditTournamentForm = combine(
  withContext(
    'taxonomyService',
    ServicesContext,
    ({ taxonomyService }) => taxonomyService
  ),

  withFormModel(
    (_state, { initialValues = {} }) => {
      return {
        sportType: {
          initialValue: initialValues?.sportType
            ? {
                id: initialValues.sportType,
                name:
                  SPORTS.find((s) => s.value === initialValues.sportType)
                    ?.label || initialValues.sportType,
              }
            : '',
          spec: spec.flow(any.required),
        },
        tournamentName: {
          initialValue: initialValues?.tournamentName || '',
          spec: spec.flow(any.required),
        },
        tournamentFullName: {
          initialValue: initialValues?.tournamentFullName || '',
          spec: spec.flow(any.required),
        },
        logo: {
          initialValue: initialValues?.logo || '',
          spec: spec.flow(any.required),
        },
        largeLogo: {
          initialValue: initialValues?.largeLogo || '',
          spec: spec.flow(any.required),
        },
        jurisdiction: {
          initialValue: initialValues?.jurisdiction || '',
          spec: spec.flow(any.required),
        },
        country: {
          initialValue: initialValues.country || '',
          spec: spec.flow(any.required),
        },
        city: {
          initialValue: initialValues?.city || '',
          spec: spec.flow(any.required),
        },
        venue: {
          initialValue: initialValues?.venue || '',
          spec: spec.flow(any.required),
        },
        website: {
          initialValue: initialValues?.website || '',
          spec: spec.flow(any.required),
        },
        marketingPromotion: {
          initialValue: initialValues?.marketingPromotion || false,
          spec: spec.flow(any.required),
        },
        startTime: {
          initialValue: initialValues?.startTime || '',
          spec: spec.flow(any.required),
        },
        startTimeHour: {
          initialValue: initialValues?.startTimeHour || '',
          spec: spec.flow(any.required),
        },
        endTime: {
          initialValue: initialValues?.endTime || '',
          spec: spec.flow(any.required),
        },
        endTimeHour: {
          initialValue: initialValues?.endTimeHour || '',
          spec: spec.flow(any.required),
        },
        followed: {
          initialValue: initialValues?.followed ?? false,
          spec: spec.flow(),
        },
        id: {
          initialValue: initialValues?.id || '',
          spec: spec.flow(),
        },
      }
    },
    { id: 'tournaments-edit-form' }
  ),

  withCallbacks(
    {
      loadCategories: () => async () => {
        const formattedSports = SPORTS.map((sport) => ({
          id: sport.value,
          name: sport.label,
        }))
        return formattedSports
      },

      getOptionLabel: () => (option) => option?.name || '',
      getOptionValue: () => (option) => option?.id || '',

      loadJurisdictions:
        ({ taxonomyService }) =>
        (search = '') =>
          taxonomyService.queryJurisdictions({ search }),

      onSubmitRequest: {
        deps: ['onSubmit'],
        func:
          (_state, { onSubmit }) =>
          (form) => {
            // Format the data before submitting
            const formData = { ...form }

            // Convert sportType back to string
            if (formData.sportType?.id) {
              formData.sportType = formData.sportType.id
            }

            // Convert country back to string
            if (formData.country?.name) {
              formData.country = formData.country.name
            }

            // Convert jurisdiction select option to jurisdictionId
            if (formData.jurisdiction?.id) {
              formData.jurisdictionId = formData.jurisdiction.id
            }
            delete formData.jurisdiction

            // Combine date and time for startTime
            if (formData.startTime) {
              const startDate = new Date(formData.startTime)
              if (formData.startTimeHour) {
                const [hours, minutes] = formData.startTimeHour
                  .split(':')
                  .map(Number)
                startDate.setHours(hours, minutes, 0, 0)
              } else {
                startDate.setHours(0, 0, 0, 0)
              }
              formData.startTime = startDate.toISOString()
            }

            // Combine date and time for endTime
            if (formData.endTime) {
              const endDate = new Date(formData.endTime)
              if (formData.endTimeHour) {
                const [hours, minutes] = formData.endTimeHour
                  .split(':')
                  .map(Number)
                endDate.setHours(hours, minutes, 0, 0)
              } else {
                endDate.setHours(23, 59, 59, 999)
              }
              formData.endTime = endDate.toISOString()
            }

            // Remove the time-only fields as they're now combined
            delete formData.startTimeHour
            delete formData.endTimeHour

            return onSubmit(formData)
          },
      },
    },
    []
  )
)(EditTournamentFormComponent)
